import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card as MuiCard,
  Avatar,
} from '@mui/material';
import {
  Shield,
  NotificationsActive,
  UndoRounded,
  Security,
} from '@mui/icons-material';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import TextField from '../../components/common/TextField/TextField';
import { useMetrics } from '../../hooks/useMetrics';
import { getCurrentAuthUser } from '../../utils/auth';
import { createWallet } from '../../utils/api';
import { STAGE } from '../../config';
import { trackEvent, trackPageView } from '../../utils/analytics';
import { formatNumber } from '../../utils/format';

const HeroSection = styled(Box)`
  background: linear-gradient(135deg, #3B3486 0%, #2A2561 100%);
  padding: 6rem 0;
  color: white;
  text-align: center;
`;

const StyledContainer = styled(Container)`
  max-width: 900px;
`;

const InputContainer = styled(Box)`
  max-width: 900px;
  margin: 0 auto;
  padding-top: 1rem;
  padding-bottom: 1rem;
`;

const InputWrapper = styled(Box)`
  position: relative;
  max-width: 600px;
  margin: 0 auto;
`;

const Title = styled(Typography)`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const Subtitle = styled(Typography)`
  font-size: 1.25rem;
  opacity: 0.9;
  margin-bottom: 3rem;
`;

const ButtonGroup = styled(Box)`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  justify-content: center;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }
`;

const PaymentCard = styled(Box)<{ $primary?: boolean }>`
  background: ${({ $primary }) => $primary ? 
    'linear-gradient(135deg, #FF6B00 0%, #FF8533 100%)' : 
    'rgba(255, 255, 255, 0.15)'
  };
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 0.75rem 1.25rem;
  min-width: 180px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  border: 1px solid ${({ $primary }) => $primary ? 'transparent' : 'rgba(255, 255, 255, 0.3)'};
  box-shadow: ${({ $primary }) => $primary ? 
    '0 2px 8px rgba(255, 107, 0, 0.2)' : 
    '0 2px 8px rgba(0, 0, 0, 0.1)'
  };

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ $primary }) => $primary ? 
      '0 4px 12px rgba(255, 107, 0, 0.3)' : 
      '0 4px 12px rgba(0, 0, 0, 0.15)'
    };
    background: ${({ $primary }) => $primary ? 
      'linear-gradient(135deg, #FF6B00 0%, #FF8533 100%)' : 
      'rgba(255, 255, 255, 0.2)'
    };
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 600px) {
    width: 100%;
    max-width: 240px;
  }
`;

const DiscountBadge = styled(Box)`
  position: absolute;
  top: -10px;
  right: -10px;
  background: white;
  color: #FF6B00;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const PaymentButtonContent = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const PaymentMethod = styled(Typography)<{ $primary?: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ $primary }) => $primary ? 'white' : 'white'};
  margin-bottom: 0.25rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const PriceContainer = styled(Box)`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
`;

const PriceTag = styled(Typography)<{ $primary?: boolean }>`
  font-size: 1.5rem;
  font-weight: 800;
  color: white;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const AnnualLabel = styled(Typography)<{ $primary?: boolean }>`
  font-size: 0.75rem;
  color: ${({ $primary }) => $primary ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.95)'};
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const FeatureCard = styled(MuiCard)`
  padding: 2rem;
  height: 100%;
  text-align: center;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const IconWrapper = styled(Box)`
  background: ${({ theme }) => theme.palette.primary.light};
  color: white;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;

  svg {
    font-size: 32px;
  }
`;

const MetricsSection = styled(Box)`
  padding: 6rem 0;
  text-align: center;
`;

const MetricsCard = styled(Box)`
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  padding: 3rem;
`;

const TestimonialsSection = styled(Box)`
  padding: 6rem 0;
  background-color: ${({ theme }) => theme.palette.background.default};
`;

const TestimonialCard = styled(Box)`
  padding: 2rem;
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  height: 100%;
`;

const Home = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { metrics, loading, error: metricsError } = useMetrics();
  const [masterKey, setMasterKey] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Track funnel start
    const checkAuthAndTrack = async () => {
      const user = await getCurrentAuthUser();
      trackPageView('/', {
        funnel_stage: 'wallet_creation_start',
        user_status: user ? 'logged_in' : 'not_logged_in'
      });
    };
    
    checkAuthAndTrack();
  }, []);

  useEffect(() => {
    // Track metrics loading status
    if (!loading) {
      trackEvent('metrics_loaded', {
        success: !metricsError,
        error: metricsError || undefined
      });
    }
  }, [loading, metricsError]);

  const validateMasterKey = (key: string): boolean => {
    const trimmedKey = key.trim();
    // Check if key is 96 characters
    if (trimmedKey.length === 96) {
      return true;
    }
    // Check if key is 62 characters and starts with xch
    if (trimmedKey.length === 62 && trimmedKey.startsWith('xch')) {
      return true;
    }
    return false;
  };

  const handleSubmit = async (paymentMethod: 'STRIPE' | 'STR' | 'XCH') => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    try {
      if (!masterKey) {
        throw new Error('Please provide a master key');
      }

      // Track payment method selection
      trackEvent('funnel_step', {
        step_name: 'payment_method_selected'
      });

      const user = await getCurrentAuthUser();
      if (!user?.username || !user?.userId) {
        // Track auth redirect
        trackEvent('funnel_step', {
          step_name: 'auth_required'
        });
        navigate('/auth');
        return;
      }

      const response = await createWallet(
        masterKey,
        user.userId,
        user.username,
        paymentMethod,
        STAGE !== 'production'
      );
      
      if (response.paid) {
        trackEvent('funnel_step', {
          step_name: 'wallet_paid'
        });
        navigate('/wallets');
        return;
      }

      if (response.stripePayment) {
        trackEvent('funnel_step', {
          step_name: 'redirect_to_stripe'
        });
        window.location.href = response.stripePayment.checkoutUrl;
        return;
      }

      if (response.cryptoPayment) {
        trackEvent('funnel_step', {
          step_name: 'redirect_to_crypto_payment'
        });
        navigate('/payment', { 
          state: { 
            walletId: response.walletId,
            masterKey: masterKey,
            paymentInfo: response.cryptoPayment
          }
        });
        return;
      }
    } catch (error) {
      trackEvent('funnel_step', {
        step_name: 'wallet_creation_error'
      });
      setError(error instanceof Error ? error.message : 'Failed to create wallet');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMasterKey(value);
    setError('');
    setValidationMessage('');
  };

  const handleAddressSubmit = (address: string) => {
    // Track address submission attempt
    trackEvent('address_submit', {
      input_length: address.length,
      is_master_key: address.length === 96
    });
    
    setIsSubmitting(true);
    setError(null);
    
    // Your existing address submission logic
    try {
      // Validate and process the address
      if (address.length === 96) {
        // Process as master key
      } else {
        // Process as XCH address
      }
    } catch (err: any) {
      setError(err.message);
      trackEvent('address_submit_error', {
        error: err.message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCardPayment = async () => {
    if (!masterKey.trim()) {
      setValidationMessage('Please enter your master key or XCH address');
      return;
    }
    if (!isSubmitting) {
      handleSubmit('STRIPE');
    }
  };

  const handleStrPayment = () => {
    trackEvent('payment_initiated', {
      method: 'str'
    });
    navigate('/payment?method=str');
  };

  return (
    <>
      <HeroSection>
        <StyledContainer>
          <Title variant="h1">
            {t('home.hero.title')}
          </Title>
          <Subtitle variant="h5">
            {t('home.hero.subtitle')}
          </Subtitle>
          <InputContainer>
            <Box mt={4}>
              <TextField
                fullWidth
                value={masterKey}
                onChange={handleInputChange}
                error={!!error || !!validationMessage}
                helperText={validationMessage || error}
                placeholder={t('home.hero.input.placeholder')}
                disabled={isSubmitting}
                sx={{ maxWidth: '600px', mx: 'auto' }}
              />

              <ButtonGroup>
                <PaymentCard
                  $primary
                  onClick={() => {
                    if (!masterKey.trim()) {
                      setValidationMessage('Please enter your master key or XCH address');
                      return;
                    }
                    if (!isSubmitting) {
                      handleSubmit('STR');
                    }
                  }}
                  style={{ opacity: isSubmitting ? 0.7 : 1 }}
                >
                  <DiscountBadge>SAVE 50%</DiscountBadge>
                  <PaymentButtonContent>
                    <PriceContainer>
                      <PriceTag $primary>$6</PriceTag>
                      <AnnualLabel $primary>/ Year</AnnualLabel>
                    </PriceContainer>
                    <PaymentMethod $primary>Pay with STR</PaymentMethod>
                  </PaymentButtonContent>
                </PaymentCard>

                <PaymentCard
                  onClick={() => {
                    if (!masterKey.trim()) {
                      setValidationMessage('Please enter your master key or XCH address');
                      return;
                    }
                    if (!isSubmitting) {
                      handleCardPayment();
                    }
                  }}
                  style={{ opacity: isSubmitting ? 0.7 : 1 }}
                >
                  <PaymentButtonContent>
                    <PriceContainer>
                      <PriceTag>$12</PriceTag>
                      <AnnualLabel>/ Year</AnnualLabel>
                    </PriceContainer>
                    <PaymentMethod>Pay with Stripe</PaymentMethod>
                  </PaymentButtonContent>
                </PaymentCard>
              </ButtonGroup>
            </Box>
          </InputContainer>
        </StyledContainer>
      </HeroSection>

      <Container maxWidth="lg">
        <Box py={8}>
          <Typography variant="h3" align="center" gutterBottom>
            {t('home.features.title')}
          </Typography>
          <Typography variant="h6" align="center" color="textSecondary" paragraph>
            {t('home.features.subtitle')}
          </Typography>

          <Grid container spacing={4} mt={4}>
            <Grid item xs={12} md={6} lg={3}>
              <FeatureCard>
                <IconWrapper>
                  <Security />
                </IconWrapper>
                <Typography variant="h6" gutterBottom>
                  {t('home.features.vault.title')}
                </Typography>
                <Typography color="textSecondary">
                  {t('home.features.vault.description')}
                </Typography>
              </FeatureCard>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <FeatureCard>
                <IconWrapper>
                  <Shield />
                </IconWrapper>
                <Typography variant="h6" gutterBottom>
                  {t('home.features.monitoring.title')}
                </Typography>
                <Typography color="textSecondary">
                  {t('home.features.monitoring.description')}
                </Typography>
              </FeatureCard>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <FeatureCard>
                <IconWrapper>
                  <NotificationsActive />
                </IconWrapper>
                <Typography variant="h6" gutterBottom>
                  {t('home.features.alerts.title')}
                </Typography>
                <Typography color="textSecondary">
                  {t('home.features.alerts.description')}
                </Typography>
              </FeatureCard>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <FeatureCard>
                <IconWrapper>
                  <UndoRounded />
                </IconWrapper>
                <Typography variant="h6" gutterBottom>
                  {t('home.features.recovery.title')}
                </Typography>
                <Typography color="textSecondary">
                  {t('home.features.recovery.description')}
                </Typography>
              </FeatureCard>
            </Grid>
          </Grid>
        </Box>
      </Container>

      <MetricsSection>
        <Container maxWidth="lg">
          <MetricsCard>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Typography variant="h3" color="primary" gutterBottom>
                  {loading ? '...' : formatNumber(metrics?.walletsMonitored || 0)}
                </Typography>
                <Typography variant="h6" color="textSecondary">
                  {t('home.metrics.walletsMonitored.label')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h3" color="primary" gutterBottom>
                  {loading ? '...' : formatNumber(metrics?.alertsSent || 0)}
                </Typography>
                <Typography variant="h6" color="textSecondary">
                  {t('home.metrics.alertsSent.label')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h3" color="primary" gutterBottom>
                  {loading ? '...' : formatNumber(metrics?.transactionsProcessed || 0)}
                </Typography>
                <Typography variant="h6" color="textSecondary">
                  {t('home.metrics.transactionsAnalyzed.label')}
                </Typography>
              </Grid>
            </Grid>
            {error && (
              <Typography color="error" align="center" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
          </MetricsCard>
        </Container>
      </MetricsSection>

      <TestimonialsSection>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom>
            {t('home.testimonials.title')}
          </Typography>
          <Grid container spacing={4} mt={2}>
            {[0, 1, 2].map((index) => (
              <Grid item xs={12} md={4} key={index}>
                <TestimonialCard>
                  <Typography variant="body1" paragraph>
                    "{t(`home.testimonials.items.${index}.quote`)}"
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar>{t(`home.testimonials.items.${index}.author`)[0]}</Avatar>
                    <Box>
                      <Typography variant="subtitle2">
                        {t(`home.testimonials.items.${index}.author`)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t(`home.testimonials.items.${index}.role`)}
                      </Typography>
                    </Box>
                  </Box>
                </TestimonialCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </TestimonialsSection>
    </>
  );
};

export default Home;